import { useEffect, useRef, useState } from 'react';
import { Box, CircularProgress, IconButton, Stack, Typography } from '@mui/material';
import { keyframes, styled } from '@mui/material/styles';
import { Check, Copy, RotateCcw, ThumbsDown, ThumbsUp } from 'lucide-react';
import { useReducedMotion } from 'framer-motion';
import { QuickQuestion } from './lib';
import { useChat, UseChatOptions } from './use-chat';
import { makeVar, useReactiveVar } from '@apollo/client';
import { PageContext } from '../../../../shared';
import { ChatInput, ChatInputRenderProps } from './chat-input';

export const pageContext = makeVar<PageContext | undefined>(undefined);
export const chatQuickQuestions = makeVar<QuickQuestion[] | undefined>(undefined);

// ─── Props ────────────────────────────────────────────────────────────────────

export interface ChatBubbleStreamProps extends UseChatOptions {
  suggestedLabel?: string;
  placeholder?: string;
  fillHeight?: boolean;
  renderInput?: (props: ChatInputRenderProps) => React.ReactNode;
}

// ─── Component ────────────────────────────────────────────────────────────────

export const ChatBubbleStream = ({
  suggestedLabel = 'Suggested questions:',
  placeholder = 'Ask a question...',
  fillHeight = false,
  renderInput,
  ...chatOptions
}: ChatBubbleStreamProps) => {
  const pc = useReactiveVar(pageContext);
  const qq = useReactiveVar(chatQuickQuestions);
  const prefersReduced = useReducedMotion();

  const quickQuestions = qq ?? chatOptions.quickQuestions ?? [];

  const {
    messages,
    streamingText,
    thinkingLabel,
    thinkingSteps,
    isTyping,
    hasReplies,
    input,
    setInput,
    handleSubmit,
    handleQuickQuestion,
    handleClarification,
    retryLast,
    flushTypewriter,
    lastResponseRef,
    scrollContainerRef,
  } = useChat({ ...chatOptions, pageContext: chatOptions.pageContext ?? pc });

  // ── Focus input when typewriter finishes ──────────────────────────────────
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const prevIsTypingRef = useRef(false);
  useEffect(() => {
    if (prevIsTypingRef.current && !isTyping) inputRef.current?.focus();
    prevIsTypingRef.current = isTyping;
  }, [isTyping]);

  const handleCopy = (html: string) => {
    const text = new DOMParser().parseFromString(html, 'text/html').body.textContent ?? '';
    navigator.clipboard.writeText(text).catch(() => {/* ignore */});
  };

  return (
    <StreamRoot>

      {/* ── Scrollable message area ── */}
      <MessageArea
        id="chat-message-area"
        ref={fillHeight ? scrollContainerRef : undefined}
        role="log"
        aria-live="polite"
        aria-label="Chat messages"
        $fill={fillHeight}
      >

        {/* Committed messages */}
        {messages.map((msg, i, arr) => {
          const isLastMsg      = i === arr.length - 1;
          const isLastResponse = !msg.isUser && isLastMsg;
          return (
            <MsgRow
              key={i}
              ref={isLastResponse ? lastResponseRef : undefined}
              $isUser={msg.isUser}
              $hoverable={isLastResponse}
            >
              <Bubble $isUser={msg.isUser}>
                <BubbleText
                  $isUser={msg.isUser}
                  dangerouslySetInnerHTML={{ __html: msg.text }}
                />
              </Bubble>

              {/* Hover action bar — last AI bubble only */}
              {isLastResponse && !!i && !isTyping && (
                <ActionBar className="msg-actions">
                  <ActionBtn title="Copy"         onClick={() => handleCopy(msg.text)}><Copy      size={14} /></ActionBtn>
                  <ActionBtn title="Good response"                                    ><ThumbsUp   size={14} /></ActionBtn>
                  <ActionBtn title="Bad response"                                     ><ThumbsDown size={14} /></ActionBtn>
                  <ActionBtn title="Retry"        onClick={retryLast}                ><RotateCcw  size={14} /></ActionBtn>
                </ActionBar>
              )}

              {/* Follow-up suggestions */}
              {!msg.isUser && msg.followups?.length && isLastMsg && !isTyping && (
                <SuggestionList>
                  {msg.followups.map(q => (
                    <SuggestionBtn key={q} onClick={() => handleQuickQuestion(q)}>
                      {q}
                    </SuggestionBtn>
                  ))}
                </SuggestionList>
              )}

              {/* Clarification options */}
              {!msg.isUser && msg.clarificationOptions?.length && isLastMsg && (
                <SuggestionList>
                  {msg.clarificationOptions.map(opt => (
                    <SuggestionBtn key={opt} onClick={() => handleClarification(opt)}>
                      {opt}
                    </SuggestionBtn>
                  ))}
                </SuggestionList>
              )}
            </MsgRow>
          );
        })}

        {/* ── Live streaming bubble ── */}
        {isTyping && (
          <>
            {!streamingText && (
              <MsgRow $isUser={false} $hoverable={false}>
                <ThinkingCard steps={thinkingSteps} currentLabel={thinkingLabel} />
              </MsgRow>
            )}
            {streamingText && (
              <StreamingRow onClick={flushTypewriter} title="Click to show full answer">
                <Bubble $isUser={false}>
                  <BubbleText $isUser={false} dangerouslySetInnerHTML={{ __html: streamingText }} />
                  <Cursor aria-hidden $reduced={!!prefersReduced} />
                </Bubble>
              </StreamingRow>
            )}
          </>
        )}

        {/* Quick questions — shown before conversation starts */}
        {!hasReplies && (
          <Box>
            <SuggestedLabel>{suggestedLabel}</SuggestedLabel>
            <SuggestionList>
              {quickQuestions.map((q: QuickQuestion) => (
                <SuggestionBtn key={q.id} onClick={() => handleQuickQuestion(q.label)}>
                  {q.label}
                </SuggestionBtn>
              ))}
            </SuggestionList>
          </Box>
        )}
      </MessageArea>

      {/* Thinking indicator */}
      {isTyping && !streamingText && (
        <ThinkingRow>
          <CircularProgress size={12} sx={{ color: '#094451' }} />
          <Typography sx={{ fontSize: 13, color: '#64748b' }}>
            <ElapsedTimer /> &#8226; <i>Thinking...</i>
          </Typography>
        </ThinkingRow>
      )}

      {/* ── Input area ── */}
      {(renderInput ?? (p => <ChatInput {...p} />))({
        input,
        setInput,
        handleSubmit,
        isTyping,
        placeholder,
        inputRef,
      })}

    </StreamRoot>
  );
};

// ─── ElapsedTimer ─────────────────────────────────────────────────────────────

const ElapsedTimer = () => {
  const [seconds, setSeconds] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setSeconds(s => s + 1), 1000);
    return () => clearInterval(id);
  }, []);
  return <>{seconds}s</>;
};

// ─── ThinkingCard ─────────────────────────────────────────────────────────────

interface ThinkingCardProps { steps: string[]; currentLabel: string; }

const ThinkingCard = ({ steps, currentLabel }: ThinkingCardProps) => {
  const allSteps = steps.length > 0 ? steps : currentLabel ? [currentLabel] : [];
  return (
    <ThinkingBox>
      {allSteps.map((label, i) => {
        const isActive = i === allSteps.length - 1;
        return (
          <Stack key={i} direction="row" alignItems="center" gap={1.25} py={0.375}>
            {isActive
              ? <ThinkingDot />
              : <Check size={16} color="#16a34a" />
            }
            <Typography sx={{ fontSize: 13, color: isActive ? '#334155' : '#64748b' }}>
              {label}
            </Typography>
          </Stack>
        );
      })}
    </ThinkingBox>
  );
};

// ─── Animations ───────────────────────────────────────────────────────────────

const blink = keyframes`
  0%, 100% { opacity: 1 }
  50%       { opacity: 0 }
`;

// ─── Styled ───────────────────────────────────────────────────────────────────

const StreamRoot = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  overflow: 'hidden',
});

const MessageArea = styled(Box, {
  shouldForwardProp: p => p !== '$fill',
})<{ $fill: boolean }>(({ $fill }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
  padding: 16,
  background: 'rgba(249,250,251,0.5)',
  ...($fill ? { flex: 1, minHeight: 0, overflowY: 'auto' } : {}),
  // hide scrollbar
  scrollbarWidth: 'none',
  '&::-webkit-scrollbar': { display: 'none' },
}));

const MsgRow = styled(Box, {
  shouldForwardProp: p => p !== '$isUser' && p !== '$hoverable',
})<{ $isUser: boolean; $hoverable: boolean }>(({ $isUser, $hoverable }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: $isUser ? 'flex-end' : 'flex-start',
  ...($hoverable && { '&:hover .msg-actions': { opacity: 1, pointerEvents: 'auto' } }),
}));

const Bubble = styled(Box, {
  shouldForwardProp: p => p !== '$isUser',
})<{ $isUser: boolean }>(({ $isUser }) => ({
  maxWidth: '85%',
  boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
  overflowWrap: 'break-word',
  ...($isUser
    ? {
        borderRadius: '16px 2px 16px 16px',
        padding: 16,
        background: '#2563eb',
        border: 'none',
      }
    : {
        borderRadius: '4px 16px 16px 16px',
        padding: '10px 12px',
        background: 'rgba(239,246,255,0.5)',
        border: '1px solid #bfdbfe',
      }),
}));

const BubbleText = styled(Box, {
  shouldForwardProp: p => p !== '$isUser',
})<{ $isUser: boolean }>(({ $isUser }) => ({
  fontSize: 12,
  lineHeight: 1.6,
  color: $isUser ? '#ffffff' : '#374151',
  fontWeight: $isUser ? 500 : 400,
}));

const StreamingRow = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  cursor: 'pointer',
});

const Cursor = styled('span', {
  shouldForwardProp: p => p !== '$reduced',
})<{ $reduced: boolean }>(({ $reduced }) => ({
  display: 'inline-block',
  width: 2,
  height: '1em',
  background: '#475569',
  marginLeft: 4,
  verticalAlign: 'text-bottom',
  animation: $reduced ? 'none' : `${blink} 1s step-end infinite`,
}));

const ActionBar = styled(Stack)({
  opacity: 0,
  pointerEvents: 'none',
  transition: 'opacity 150ms',
  flexDirection: 'row',
  gap: 2,
  marginTop: 4,
});

const SuggestedLabel = styled(Typography)({
  fontSize: '0.625rem',
  fontWeight: 700,
  color: '#9ca3af',
  marginBottom: 8,
  letterSpacing: '0.04em',
});

const SuggestionList = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
  width: '100%',
});

const SuggestionBtn = styled('button')({
  width: '100%',
  textAlign: 'left',
  background: '#eff6ff',
  border: '1px solid transparent',
  borderRadius: 12,
  padding: '10px 12px',
  fontSize: '0.75rem',
  fontWeight: 500,
  color: '#1e3a8a',
  cursor: 'pointer',
  transition: 'background 150ms',
  fontFamily: 'inherit',
  lineHeight: 1.4,
  '&:hover': { background: '#dbeafe' },
});

const ThinkingRow = styled(Box)({
  background: '#ffffff',
  borderRadius: 12,
  padding: '10px 20px',
  display: 'flex',
  alignItems: 'center',
  gap: 16,
});

const ThinkingBox = styled(Box)({
  background: '#ffffff',
  border: '1px solid #e2e8f0',
  borderRadius: 12,
  padding: '12px 16px',
  minWidth: 260,
});

const ThinkingDot = styled(Box)({
  width: 8,
  height: 8,
  margin: 4,
  borderRadius: '50%',
  background: '#094451',
  flexShrink: 0,
});

const ActionBtn = styled(IconButton)({
  width: 28,
  height: 28,
  borderRadius: 6,
  color: '#94a3b8',
  '&:hover': { background: '#f1f5f9', color: '#475569' },
});
